/**
 * Eine Reiterleiste aus Adressen — dieselbe Gestalt wie die Reiter an Zähler
 * und Gerät (HM-042, HM-068).
 *
 * **Echte Links und keine Knöpfe:** Jeder Reiter hat eine Adresse, und ein
 * `<button>` davor wäre weder im neuen Tab zu öffnen noch zu kopieren. Strg-,
 * Cmd- und Mittelklick gehen deshalb an den Browser weiter. `aria-current`
 * statt `role="tab"`: Was hier steht, sind Verweise und keine Reiter eines
 * Widgets.
 */
import type { ReactNode } from "react";
import { navigate, routePath, type Route } from "../lib/router.js";

export interface RouteTab {
  readonly key: string;
  readonly label: ReactNode;
  readonly route: Route;
}

export function RouteTabs({
  label,
  tabs,
  current,
}: {
  /** Wofür die Leiste steht — wird zur `aria-label` der Navigation. */
  readonly label: string;
  readonly tabs: readonly RouteTab[];
  readonly current: string;
}) {
  return (
    <nav
      aria-label={label}
      style={{
        display: "flex",
        gap: 4,
        padding: 4,
        flexWrap: "wrap",
        background: "var(--surface-sunken)",
        borderRadius: "var(--radius-md)",
      }}
    >
      {tabs.map((tab) => {
        const active = tab.key === current;
        return (
          <a
            key={tab.key}
            href={routePath(tab.route)}
            aria-current={active ? "page" : undefined}
            onClick={(event) => {
              if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
              event.preventDefault();
              navigate(tab.route);
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 7,
              padding: "7px 14px",
              borderRadius: "var(--radius-sm)",
              textDecoration: "none",
              fontFamily: "var(--font-body)",
              fontSize: 13.5,
              fontWeight: active ? 700 : 500,
              background: active ? "var(--surface-card)" : "transparent",
              boxShadow: active ? "var(--shadow-xs)" : "none",
              color: active ? "var(--text-strong)" : "var(--text-muted)",
            }}
          >
            {tab.label}
          </a>
        );
      })}
    </nav>
  );
}
